/**
 * Queue trigger summary helpers (AC-1313).
 *
 * Pure utilities for rendering a compact trigger summary on the Queues list
 * page. Keep these framework-agnostic so they're unit-testable without
 * mounting a component.
 */
import type { Queue } from '../types';
import { INTERVAL_OPTIONS, formatTimestamp } from '../components/Queue/QueueSchedulingSettings';

export type PillTone = 'gray' | 'blue' | 'amber' | 'rose' | 'indigo';

export interface TriggerPill {
  label: string;
  tone: PillTone;
  tooltip?: string;
}

/**
 * Human label for a trigger interval. Prefers the preset strings used on the
 * details page (e.g. "Every 5m", "Every 1 hour") and falls back to a generic
 * "Every Nm" for non-preset values.
 */
export function formatIntervalLabel(minutes: number | null | undefined): string {
  if (minutes == null) return '';
  const preset = INTERVAL_OPTIONS.find((o) => o.value === String(minutes));
  if (preset && preset.label !== 'None') return preset.label;
  return `Every ${minutes}m`;
}

/**
 * Render a timestamp as a short relative duration. Positive delta (timestamp
 * in the past) is returned without a suffix; callers prepend "Last:" /
 * "Next:" as appropriate. Returns "" for null/undefined/invalid input.
 */
export function formatRelative(
  ts: string | Date | null | undefined,
  now: Date = new Date(),
): string {
  if (!ts) return '';
  const then = ts instanceof Date ? ts : new Date(ts);
  const msThen = then.getTime();
  if (isNaN(msThen)) return '';
  const diffSec = Math.abs(now.getTime() - msThen) / 1000;
  if (diffSec < 60) return '<1m';
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  return `${day}d`;
}

/**
 * Pills describing the queue's trigger configuration, in render order.
 * Returns a single "Off" pill when auto-trigger is disabled.
 */
export function triggerSummaryPills(queue: Queue): TriggerPill[] {
  if (!queue.auto_trigger_enabled) {
    return [{ label: 'Off', tone: 'gray' }];
  }

  const pills: TriggerPill[] = [];

  if (queue.trigger_interval_minutes != null) {
    pills.push({ label: formatIntervalLabel(queue.trigger_interval_minutes), tone: 'blue' });
  }

  const hasItem = queue.trigger_item_threshold != null;
  const hasAge = queue.trigger_age_threshold_minutes != null;

  if (hasItem) {
    pills.push({ label: `≥${queue.trigger_item_threshold} items`, tone: 'amber' });
  }
  if (hasAge) {
    pills.push({ label: `≥${queue.trigger_age_threshold_minutes}m old`, tone: 'amber' });
  }
  if (hasItem && hasAge && queue.trigger_condition_mode) {
    // Condition mode is only meaningful when both thresholds exist.
    pills.push({ label: `(${queue.trigger_condition_mode})`, tone: 'gray' });
  }

  if (queue.drain_mode) {
    const cycles = queue.drain_max_cycles ?? 100;
    pills.push({
      label: 'Drain',
      tone: 'rose',
      tooltip: `Loops until empty (max ${cycles} cycles)`,
    });
  }

  if (queue.completed_retention_days != null) {
    pills.push({
      label: `${queue.completed_retention_days}d retention`,
      tone: 'indigo',
      tooltip: 'Completed items older than this are auto-deleted',
    });
  }

  if (pills.length === 0) {
    // Auto-trigger is enabled but nothing else is configured — surface that
    // gap explicitly so the user knows the toggle isn't actually doing
    // anything yet.
    pills.push({ label: 'On (no conditions)', tone: 'gray' });
  }

  return pills;
}

/**
 * Secondary-line label with relative time for the next scheduled fire, or
 * the last fire if never scheduled ahead. Returns "" when neither timestamp
 * is populated.
 */
export function formatTriggerRelativeLine(queue: Queue, now: Date = new Date()): string {
  if (queue.next_trigger_at) {
    const r = formatRelative(queue.next_trigger_at, now);
    return r ? `Next: ${r}` : '';
  }
  if (queue.last_triggered_at) {
    const r = formatRelative(queue.last_triggered_at, now);
    return r ? `Last: ${r} ago` : '';
  }
  return '';
}

/**
 * Multi-line text for the full trigger tooltip. Mirrors what the Queue
 * Details Scheduling card shows so users get a consistent view.
 */
export function formatTriggerTooltip(queue: Queue): string {
  const lines: string[] = [];
  lines.push(`Auto-trigger: ${queue.auto_trigger_enabled ? 'on' : 'off'}`);
  if (queue.trigger_interval_minutes != null) {
    lines.push(`Interval: ${formatIntervalLabel(queue.trigger_interval_minutes).toLowerCase()}`);
  }
  if (queue.trigger_item_threshold != null) {
    lines.push(`Item threshold: ≥${queue.trigger_item_threshold} items`);
  }
  if (queue.trigger_age_threshold_minutes != null) {
    lines.push(`Age threshold: ≥${queue.trigger_age_threshold_minutes} minutes`);
  }
  if (
    queue.trigger_item_threshold != null &&
    queue.trigger_age_threshold_minutes != null &&
    queue.trigger_condition_mode
  ) {
    lines.push(`Condition mode: ${queue.trigger_condition_mode}`);
  }
  if (queue.drain_mode) {
    lines.push(`Drain mode: on, up to ${queue.drain_max_cycles ?? 100} cycles`);
  }
  if (queue.skip_if_empty != null) {
    lines.push(`Skip if empty: ${queue.skip_if_empty ? 'yes' : 'no'}`);
  }
  if (queue.completed_retention_days != null) {
    lines.push(`Completed retention: ${queue.completed_retention_days} days`);
  }
  lines.push(`Last triggered: ${formatTimestamp(queue.last_triggered_at as any)}`);
  lines.push(`Next scheduled: ${formatTimestamp(queue.next_trigger_at as any)}`);
  return lines.join('\n');
}

/**
 * Tailwind class set for each tone. Matches the inline pill pattern already
 * used throughout QueuesPage / CircuitBreakerIndicator.
 */
export const PILL_TONE_CLASSES: Record<PillTone, string> = {
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
};
